import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {isLoaded as isLoadedPackages, PackageModel, UnassignedPackageModel} from '../PackageModel';

type AccidentNewYorkProps = {
  packageName: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  cwa: StringUnion;
  replacement: boolean;
  section125: boolean;
  policyNumber: PolicyNumber;
  packageList: PackageModel;
  baseUnits: StringUnion;
  jStamp: boolean;
  situsState: StringUnion;
  plan: StringUnion;
};

export class InvalidNewYorkAccidentNewYork {
  public readonly key = 'InvalidNewYorkAccidentNewYork';

  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly section125: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly baseUnits: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion,
    public readonly plan: StringUnion
  ) {}
}

export class ValidNewYorkAccidentNewYork {
  public static createApp({
    packageName,
    planType,
    premium,
    cwa,
    replacement,
    section125,
    policyNumber,
    packageList,
    baseUnits,
    jStamp,
    situsState,
    plan,
  }: AccidentNewYorkProps) {
    return this.create(
      packageName,
      planType,
      premium,
      cwa,
      replacement,
      section125,
      policyNumber,
      packageList,
      baseUnits,
      jStamp,
      situsState,
      plan
    );
  }

  public static create(
    packageName: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    cwa: StringUnion,
    replacement: boolean,
    section125: boolean,
    policyNumber: PolicyNumber,
    packageList: PackageModel,
    baseUnits: StringUnion,
    jStamp: boolean,
    situsState: StringUnion,
    plan: StringUnion
  ) {
    return isNonEmptyString(packageName) &&
      isNonEmptyString(planType) &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      section125 &&
      replacement &&
      policyNumber &&
      isLoadedPackages(packageList) &&
      isNonEmptyString(plan)
      ? new ValidNewYorkAccidentNewYork(
          packageName,
          planType,
          premium,
          cwa,
          replacement,
          section125,
          policyNumber,
          packageList,
          baseUnits,
          jStamp,
          situsState,
          plan
        )
      : new InvalidNewYorkAccidentNewYork(
          packageName,
          planType,
          premium,
          cwa,
          replacement,
          section125,
          policyNumber,
          packageList,
          baseUnits,
          jStamp,
          situsState,
          plan
        );
  }

  public readonly key = 'ValidNewYorkAccidentNewYork';
  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly section125: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly baseUnits: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion,
    public readonly plan: StringUnion
  ) {}
}

export type NewYorkAccidentNewYork = InvalidNewYorkAccidentNewYork | ValidNewYorkAccidentNewYork;

export type NewYorkAccidentNewYorkOptions<TReturn> = ToPartialMatchOptions<NewYorkAccidentNewYork, 'key', TReturn>;

export const matchNewYorkAccidentNewYork = <TReturn>(options: NewYorkAccidentNewYorkOptions<TReturn>) => (
  app: NewYorkAccidentNewYork
): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkAccidentNewYork':
      return options.InvalidNewYorkAccidentNewYork ? options.InvalidNewYorkAccidentNewYork(app) : options.default(app);
    case 'ValidNewYorkAccidentNewYork':
      return options.ValidNewYorkAccidentNewYork ? options.ValidNewYorkAccidentNewYork(app) : options.default(app);
  }
};

export const EmptyNewYorkAccidentNewYork = new InvalidNewYorkAccidentNewYork(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new UnassignedPackageModel(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString()
);

export const changePackage = (packageName: string) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, packageName: NonEmptyString.create(packageName)});

export const changePlanType = (planType: string) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCWA = (cwa: string) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeReplacement = (replacement: boolean) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({
    ...app,
    replacement,
    policyNumber: ValidPolicyNumber.create(new EmptyString()),
  });

export const changeSection125 = (section125: boolean) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, section125});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changePackageList = (packages: PackageModel) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, packageList: packages});

export const changeBaseUnits = (baseUnits: string) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, baseUnits: NonEmptyString.create(baseUnits)});

export const changeJStamp = (jStamp: boolean) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, jStamp});

export const changeSitusState = (situsState: string) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changePlan = (plan: string) => (app: NewYorkAccidentNewYork) =>
  ValidNewYorkAccidentNewYork.createApp({...app, plan: NonEmptyString.create(plan)});
